import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, Loader2, Trash2, GripVertical } from 'lucide-react';
import { uploadImage, deleteRemoteFile } from '@/lib/uploader';
import { cn } from '@/lib/utils';

/**
 * MultiImageUploader — manage an ordered list of image URLs.
 *
 * Props:
 * - value: string[]
 * - onChange: (nextArray) => void
 * - prefix?: string
 * - testId?: string
 */
export const MultiImageUploader = ({
    value = [],
    onChange,
    prefix,
    testId,
    className = '',
}) => {
    const [uploading, setUploading] = useState(false);
    const items = Array.isArray(value) ? value : [];

    const addFiles = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of Array.from(files)) {
                if (!f.type.startsWith('image/')) continue;
                const res = await uploadImage(f, prefix);
                uploaded.push(res.url);
            }
            onChange && onChange([...items, ...uploaded]);
            toast.success(`Uploaded ${uploaded.length} image${uploaded.length === 1 ? '' : 's'}`);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || 'Upload failed';
            toast.error(msg);
        } finally {
            setUploading(false);
        }
    };

    const removeAt = async (idx) => {
        const url = items[idx];
        try {
            await deleteRemoteFile(url);
        } catch (_) {
            // silent
        }
        const next = items.filter((_, i) => i !== idx);
        onChange && onChange(next);
    };

    const moveItem = (from, to) => {
        if (to < 0 || to >= items.length) return;
        const next = [...items];
        const [x] = next.splice(from, 1);
        next.splice(to, 0, x);
        onChange && onChange(next);
    };

    return (
        <div className={cn('space-y-3', className)} data-testid={testId}>
            <label
                className={cn(
                    'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-5 cursor-pointer text-center transition-colors',
                    'border-border bg-[hsl(var(--secondary))]/30 hover:bg-[hsl(var(--secondary))]/60'
                )}
                data-testid={testId ? `${testId}-dropzone` : undefined}
            >
                <div className="h-10 w-10 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center mb-2">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                </div>
                <div className="text-sm font-medium text-foreground">
                    {uploading ? 'Uploading…' : 'Click or drop images (multi-select)'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP · up to 8 MB each</div>
                <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => addFiles(e.target.files)}
                />
            </label>

            {items.length > 0 && (
                <div
                    className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                    data-testid={testId ? `${testId}-grid` : undefined}
                >
                    {items.map((url, idx) => (
                        <Card key={`${url}-${idx}`} className="overflow-hidden bg-card border-border">
                            <CardContent className="p-0">
                                <div className="aspect-[4/3] bg-muted overflow-hidden">
                                    <img src={url} alt="" className="w-full h-full object-cover" />
                                </div>
                                <div className="flex items-center justify-between p-2">
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => moveItem(idx, idx - 1)}
                                            disabled={idx === 0}
                                            data-testid={testId ? `${testId}-move-up-${idx}` : undefined}
                                            title="Move up"
                                        >
                                            <GripVertical className="h-3.5 w-3.5 rotate-90" />
                                        </Button>
                                        <span className="text-[10px] text-muted-foreground">#{idx + 1}</span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-destructive hover:text-destructive"
                                        onClick={() => removeAt(idx)}
                                        data-testid={testId ? `${testId}-remove-${idx}` : undefined}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiImageUploader;
